import { ActionButtonText } from '@strata/react-buttons';
import {
    MemberEscalationSubType,
    MemberInquiryEscalationOptionTree,
    RequestType,
} from '@strata/react-chat';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { Hyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import * as Yup from 'yup';

import { PBMOnlySelector } from 'components/requests/dialogs/PBMOnlySelector';
import { AgoraTreatmentBaseUrl } from 'services/constants';
import { useGetAllProductServicesQuery } from 'store/api/referenceApi';
import { useLazyGetTreatmentsForPatientQuery } from 'store/api/treatmentApi';

const validationSchemaShape = (typeLevel2IsNeeded: boolean) => ({
    segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
    requestType: Yup.string()
        .oneOf(Object.values(RequestType).filter((type) => type !== RequestType.ARCHIVE))
        .required(validationStrings.requiredField.errorMessage),
    requestSubType: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    typeLevel2: Yup.string().when(['requestType'], {
        is: (requestType: string) => requestType === RequestType.ESCALATION && typeLevel2IsNeeded,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    memberId: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    firstName: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    lastName: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    dateOfBirth: DateInputFormik.validator().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    treatments: Yup.array(
        Yup.object({
            treatment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
            product_service_oid: Yup.string().required(
                validationStrings.requiredField.errorMessage,
            ),
        }),
    ).when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),

    selectedTreatments: Yup.array(),
});

const valuesOfRequestSubType = MemberInquiryEscalationOptionTree.filter((branch) =>
    Object.values(MemberEscalationSubType).find((st) => st === branch.ticketType),
).map((item) => ({
    name: item.ticketType,
    secondaryData: item.typeDescription,
}));

const subTypeSelectValue = (name: string | undefined) =>
    // return a copy of the readonly data as a mutable to be
    // able to be used with select drop down.
    MemberInquiryEscalationOptionTree.find((item) => item.ticketType === name)?.typeLevel2?.map(
        (item) => ({ name: item.name, secondaryData: item.secondaryData }),
    );

interface MemberEscalationProps {
    payerId?: string;
    requestDetailsPBMOnly?: boolean;
    isNewRequest?: boolean;
    pbmUsersPresent: boolean;
}

export const MemberEscalation = ({
    payerId = undefined,
    requestDetailsPBMOnly = undefined,
    isNewRequest = undefined,
    pbmUsersPresent,
}: MemberEscalationProps) => {
    const { showPayerRequestSubSubTypes } = useFlags<{ showPayerRequestSubSubTypes: boolean }>();
    const [switchedToPBMOnly, setSwitchedToPBMOnly] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const ValidationSchema = Yup.object(validationSchemaShape(showPayerRequestSubSubTypes));
    const formik = useFormikContext<Yup.InferType<typeof ValidationSchema>>();
    const [getTreatmentsForPatient] = useLazyGetTreatmentsForPatientQuery();
    const { data: products } = useGetAllProductServicesQuery();

    return !!payerId && formik.values.requestType === RequestType.ESCALATION ? (
        <div className="margin__bottom--medium">
            {showPayerRequestSubSubTypes && (
                <SuggestSingleSelectFormik
                    id="requestSubType"
                    label="Please Select One Of The Following"
                    values={valuesOfRequestSubType}
                    onChange={(newval) => {
                        (() =>
                            formik.setValues({
                                ...formik.values,
                                requestSubType: newval?.name,
                                typeLevel2:
                                    newval?.name === formik.values.requestSubType
                                        ? formik.values.typeLevel2
                                        : undefined,
                            }))();
                    }}
                    required
                    displaySecondaryDataBelowOption
                />
            )}
            {!showPayerRequestSubSubTypes && (
                <SuggestSingleSelectFormik
                    id="requestSubType"
                    label="Please Select One Of The Following"
                    values={Object.values(MemberEscalationSubType)}
                    required
                />
            )}
            {showPayerRequestSubSubTypes && formik.values.requestSubType && (
                <SuggestSingleSelectFormik
                    id="typeLevel2"
                    label="Subtype"
                    values={subTypeSelectValue(formik.values.requestSubType)}
                    onChange={(selectedItem) => {
                        (async () => {
                            await formik.setFieldValue('typeLevel2', selectedItem?.name);
                        })();
                    }}
                    required
                    displaySecondaryDataBelowOption
                />
            )}
            {pbmUsersPresent && (
                <PBMOnlySelector
                    onChange={(newVal) => {
                        (() => formik.setFieldValue('pbmOnly', newVal))();
                        (() => formik.setFieldTouched('payerAssignedUpn', true, true))();
                        if (!isNewRequest) {
                            setSwitchedToPBMOnly(newVal === 'true' && !requestDetailsPBMOnly);
                        }
                    }}
                />
            )}

            {switchedToPBMOnly && (
                <div className="margin__bottom--medium margin__top--xx-small">
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label="Non-PBM users may have already viewed the contents of this request and/or have received notifications regarding it."
                        wrapText
                    />
                </div>
            )}
            <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                Member Lookup
            </div>
            <TextInputFormik
                id="memberId"
                label="Member ID"
                required
                disabled={!!formik.values.treatments}
            />
            <div className="flex__gap--medium">
                <TextInputFormik
                    id="firstName"
                    label="Member First Name"
                    required
                    disabled={!!formik.values.treatments}
                />
                <TextInputFormik
                    id="lastName"
                    label="Member Last Name"
                    required
                    disabled={!!formik.values.treatments}
                />
                <DateInputFormik
                    id="dateOfBirth"
                    label="Member DOB"
                    required
                    disabled={!!formik.values.treatments}
                />
            </div>
            {formik.values.treatments?.length === 0 && (
                <div className="margin__bottom--medium">
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.WARNING}
                        label="No treatments found for member."
                    />
                </div>
            )}
            {!formik.values.treatments ? (
                <ActionButtonText
                    label="Lookup Member"
                    className="margin__bottom--medium"
                    disabled={
                        !!(
                            formik.errors.memberId ??
                            formik.errors.firstName ??
                            formik.errors.lastName ??
                            formik.errors.dateOfBirth
                        )
                    }
                    onClick={() => {
                        (async () => {
                            const result = await getTreatmentsForPatient({
                                payerId,
                                segmentId: formik.values.segmentId,
                                memberId: formik.values.memberId!,
                                patientFirstName: formik.values.firstName!,
                                patientLastName: formik.values.lastName!,
                                patientDob: formik.values.dateOfBirth!,
                            }).unwrap();
                            await formik.setFieldValue('treatments', result);
                        })();
                    }}
                />
            ) : (
                <ActionButtonText
                    label="Reset Member Lookup"
                    className="margin__bottom--medium"
                    onClick={() => {
                        (() =>
                            formik.setValues({
                                ...formik.values,
                                memberId: undefined,
                                firstName: undefined,
                                lastName: undefined,
                                dateOfBirth: undefined,
                                treatments: undefined,
                                selectedTreatments: undefined,
                            }))();
                    }}
                />
            )}
            {formik.values.treatments && formik.values.treatments.length > 0 && (
                <>
                    <div className="sds_label font-semi-bold--uppercase margin__bottom--small">
                        Select Treatments(s)
                    </div>
                    <CheckboxListFormik
                        name="selectedTreatments"
                        label=""
                        options={formik.values.treatments.map((treatment) => ({
                            value: treatment.treatment_id,
                            component: (
                                <div
                                    className="flex__gap--small"
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <span>
                                        {products?.productServiceMap[treatment.product_service_oid]}
                                    </span>
                                    <Hyperlink
                                        navUrl={`${AgoraTreatmentBaseUrl(payerId)}${treatment.treatment_id}`}
                                        openInNewTab
                                        linkText="View Treatment"
                                        id="view-treatment-link"
                                    />
                                </div>
                            ),
                        }))}
                    />
                </>
            )}
        </div>
    ) : null;
};

MemberEscalation.validationSchema = validationSchemaShape;
